/**
 * Trip Checklist Component
 * 
 * Main component that displays both Planning Checklist and Packing List
 * with auto-population from trip data, bookings, documents, and weather
 */

import { useEffect, useState, useCallback } from 'react';
import { View, ActivityIndicator } from 'react-native';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Caption, ThemedText } from '@/components/ui/Text';
import { PlanningChecklistSection } from './PlanningChecklistSection';
import { PackingListSection } from './PackingListSection';
import { AddChecklistItemModal } from './AddChecklistItemModal';
import {
  subscribeToTripChecklist,
  createChecklist,
  addChecklistItem,
  updateChecklistItem,
  generatePlanningChecklist,
  generatePackingList,
  type Checklist,
  type ChecklistItem,
} from '@/services/checklists';
import { getTravelRequirements } from '@/services/travelRequirements';
import { getWeatherForPacking } from '@/services/packingSuggestions';
import {
  getCityContextPackingSuggestions,
  getCityContextPlaceLabel,
  getCityContextPlanningSuggestions,
  type CityContextPlanningSuggestion,
} from '@/services/cityContextPackingSuggestions';
import type { WeatherForecast } from '@/services/weather';

type ForecastDay = WeatherForecast['forecast'][number];
import { subscribeToUserDocuments, type Document } from '@/services/documentVault';
import type { Trip } from '@/services/trips';
import type { Booking } from '@/services/bookings';

type TripChecklistProps = {
  trip: Trip;
  bookings: Booking[];
  userId: string;
  passportCountry?: string;
  onViewDocument?: (documentId: string) => void;
};

export function TripChecklist({
  trip,
  bookings,
  userId,
  passportCountry,
  onViewDocument,
}: TripChecklistProps) {
  const theme = useTheme();
  const [checklist, setChecklist] = useState<Checklist | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [weatherRetryAttempt, setWeatherRetryAttempt] = useState(0);
  const [weatherSummary, setWeatherSummary] = useState<{
    avgTemp: number;
    minTemp: number;
    maxTemp: number;
    hasPrecipitation: boolean;
    conditions: string[];
    isHistorical?: boolean;
  } | undefined>();
  const [weatherMessage, setWeatherMessage] = useState<string | undefined>();
  const [weatherBasedSuggestions, setWeatherBasedSuggestions] = useState<Array<{ title: string; notes?: string }>>([]);
  const [missingWeatherBasedSuggestionsCount, setMissingWeatherBasedSuggestionsCount] = useState(0);
  const [cityContextSuggestions, setCityContextSuggestions] = useState<Array<{ title: string; notes?: string }>>(
    [],
  );
  const [missingCityContextSuggestionsCount, setMissingCityContextSuggestionsCount] = useState(0);
  const [cityContextPlanningSuggestions, setCityContextPlanningSuggestions] = useState<
    CityContextPlanningSuggestion[]
  >([]);
  const [missingCityContextPlanningSuggestionsCount, setMissingCityContextPlanningSuggestionsCount] =
    useState(0);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [addItemGroup, setAddItemGroup] = useState<'planning' | 'packing'>('planning');

  // Simple timeout helper to avoid long hangs when upstream services are slow/unreachable.
  const withTimeout = useCallback(
    async <T,>(promise: Promise<T>, ms: number, fallback: T): Promise<T> => {
      return Promise.race([
        promise.catch(() => fallback),
        new Promise<T>((resolve) => setTimeout(() => resolve(fallback), ms)),
      ]);
    },
    [],
  );

  // Subscribe to user documents
  useEffect(() => {
    if (!userId) return;

    const unsubscribe = subscribeToUserDocuments(userId, (userDocuments) => {
      setDocuments(userDocuments);
    });

    return () => unsubscribe();
  }, [userId]);

  // Subscribe to checklist
  useEffect(() => {
    if (!trip.id) return;

    const unsubscribe = subscribeToTripChecklist(trip.id, (checklistData) => {
      setChecklist(checklistData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [trip.id]);

  // Define generateChecklistItems before using it in useEffect
  const generateChecklistItems = useCallback(async () => {
    if (!checklist || !trip.id || generating) return;

    setGenerating(true);
    try {
      // Get travel requirements
      const travelReqs = passportCountry
        ? await withTimeout(getTravelRequirements(trip, passportCountry), 6000, null)
        : null;

      // Get weather for packing (uses historical data if trip >30 days away, actual forecast if closer)
      const weatherResult = await withTimeout(
        getWeatherForPacking(trip),
        6000,
        { forecast: null, isHistorical: false, error: 'timeout' } as any,
      );
      const weather = weatherResult?.forecast || null;
      const isHistorical = weatherResult?.isHistorical;
      
      const forecastEntries: ForecastDay[] = Array.isArray(weather?.forecast)
        ? weather.forecast.filter(
            (f: ForecastDay) =>
              Number.isFinite(f?.temperature?.min) &&
              Number.isFinite(f?.temperature?.max)
          )
        : [];

      if (forecastEntries.length > 0) {
        const temps = forecastEntries.map((f: ForecastDay) => ({
          min: f.temperature.min,
          max: f.temperature.max,
        }));
        const avgMin = temps.reduce((sum: number, t) => sum + t.min, 0) / temps.length;
        const avgMax = temps.reduce((sum: number, t) => sum + t.max, 0) / temps.length;
        const avgTemp = (avgMin + avgMax) / 2;
        const minTemp = Math.min(...temps.map((t) => t.min));
        const maxTemp = Math.max(...temps.map((t) => t.max));
        const hasPrecipitation = forecastEntries.some(
          (f: ForecastDay) =>
            f.precipitation &&
            (f.precipitation.probability > 0 || (f.precipitation.amount || 0) > 0)
        );
        const conditions = forecastEntries
          .map((f: ForecastDay) => f.condition)
          .filter((c): c is string => !!c)
          .filter((v: string, i: number, a: string[]) => a.indexOf(v) === i);

        setWeatherSummary({
          avgTemp: Math.round(avgTemp),
          minTemp: Math.round(minTemp),
          maxTemp: Math.round(maxTemp),
          hasPrecipitation,
          conditions,
          isHistorical, // Track if using historical data
        });
        setWeatherMessage(undefined);
      } else {
        setWeatherSummary(undefined);
        setWeatherBasedSuggestions([]);
        setMissingWeatherBasedSuggestionsCount(0);
        if (weatherResult?.error === 'timeout') {
          setWeatherMessage('Weather is slow to load. Using a basic packing list for now.');
        } else if (weatherResult?.error) {
          console.warn('[TripChecklist] Weather fetch failed:', weatherResult.error);
          setWeatherMessage('Weather data is temporarily unavailable. We’ll refresh soon.');
        } else if (weather?.forecast) {
          console.warn('[TripChecklist] Weather forecast empty or missing temperatures');
          setWeatherMessage('Forecast not available yet. We’ll update this as the trip approaches.');
        } else {
          setWeatherMessage('Forecast not available yet. We’ll update this as the trip approaches.');
        }
      }

      // Generate planning items (always generate core items even if travelReqs fails)
      const planningItems = await generatePlanningChecklist(
        trip.id,
        userId,
        trip,
        bookings,
        documents,
        travelReqs ? {
          visaRequired: travelReqs.visaRequired,
          visaStatus: travelReqs.visaStatus,
          healthRequirements: travelReqs.healthRequirements.map(h => h.name),
          notes: travelReqs.visaNotes,
        } : undefined,
        passportCountry
      );

      // Generate packing items (always generate essentials even if weather fails)
      const packingItems = await generatePackingList(
        trip.id,
        userId,
        trip,
        weather || undefined
      );

      // Add items to checklist
      const allItems = [...planningItems, ...packingItems];
      
      // Only add items that don't already exist (by title)
      const existingTitles = new Set(checklist.items.map(item => item.title));
      const newItems = allItems.filter(item => !existingTitles.has(item.title));

      // Add items in batch to avoid race conditions
      if (newItems.length > 0) {
        console.log(`[TripChecklist] Adding ${newItems.length} new checklist items`);
        for (const item of newItems) {
          try {
            await addChecklistItem(checklist.id, item);
          } catch (error) {
            console.error(`[TripChecklist] Error adding item "${item.title}":`, error);
            // Continue with other items even if one fails
          }
        }
      } else {
        console.log('[TripChecklist] No new items to add (all items already exist)');
      }
    } catch (error) {
      console.error('[TripChecklist] Error generating checklist items:', error);
    } finally {
      setGenerating(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checklist?.id, trip.id, userId, passportCountry, bookings, documents]);

  // Build lightweight weather-based suggestions (UX layer) from the computed summary
  useEffect(() => {
    if (!checklist || !weatherSummary) {
      setWeatherBasedSuggestions([]);
      setMissingWeatherBasedSuggestionsCount(0);
      return;
    }

    const suggestions: Array<{ title: string; notes?: string }> = [];

    if (weatherSummary.maxTemp >= 25) {
      suggestions.push({ title: 'Sun hat and sunglasses', notes: `Warm days up to ${weatherSummary.maxTemp}°C` });
    }
    if (weatherSummary.maxTemp >= 30) {
      suggestions.push({ title: 'Sunscreen', notes: 'High UV exposure likely' });
    }
    if (weatherSummary.minTemp <= 10) {
      suggestions.push({ title: 'Light jacket or sweater', notes: `Cool lows around ${weatherSummary.minTemp}°C` });
    }
    if (weatherSummary.minTemp <= 5) {
      suggestions.push({ title: 'Warm accessories (scarf/hat)', notes: `Cold mornings/evenings around ${weatherSummary.minTemp}°C` });
    }
    if (weatherSummary.hasPrecipitation) {
      suggestions.push({ title: 'Umbrella or rain jacket', notes: 'Rain expected during the trip' });
    }

    // Remove any that already exist in the checklist (case-insensitive)
    const existingTitles = new Set(checklist.items.map((i) => i.title.trim().toLowerCase()));
    const missing = suggestions.filter((s) => !existingTitles.has(s.title.trim().toLowerCase()));

    setWeatherBasedSuggestions(missing);
    setMissingWeatherBasedSuggestionsCount(missing.length);
  }, [checklist?.id, checklist?.items, weatherSummary]);

  useEffect(() => {
    if (!checklist) {
      setCityContextSuggestions([]);
      setMissingCityContextSuggestionsCount(0);
      return;
    }

    const raw = getCityContextPackingSuggestions(trip);
    const existingTitles = new Set(checklist.items.map((i) => i.title.trim().toLowerCase()));
    const missing = raw.filter((s) => !existingTitles.has(s.title.trim().toLowerCase()));

    setCityContextSuggestions(missing);
    setMissingCityContextSuggestionsCount(missing.length);
  }, [checklist?.id, checklist?.items, trip]);

  useEffect(() => {
    if (!checklist) {
      setCityContextPlanningSuggestions([]);
      setMissingCityContextPlanningSuggestionsCount(0);
      return;
    }

    const raw = getCityContextPlanningSuggestions(trip);
    const existingTitles = new Set(checklist.items.map((i) => i.title.trim().toLowerCase()));
    const missing = raw.filter((s) => !existingTitles.has(s.title.trim().toLowerCase()));

    setCityContextPlanningSuggestions(missing);
    setMissingCityContextPlanningSuggestionsCount(missing.length);
  }, [checklist?.id, checklist?.items, trip]);

  const handleAddWeatherBasedSuggestions = useCallback(async () => {
    if (!checklist || generating) return;
    if (!weatherBasedSuggestions.length) return;

    try {
      const existingTitles = new Set(checklist.items.map((i) => i.title.trim().toLowerCase()));
      const toAdd = weatherBasedSuggestions.filter((s) => !existingTitles.has(s.title.trim().toLowerCase()));
      if (!toAdd.length) return;

      for (const s of toAdd) {
        await addChecklistItem(checklist.id, {
          title: s.title,
          notes: s.notes,
          group: 'packing',
          category: 'clothing',
          completed: false,
          autoGenerated: true,
          source: 'weather',
        });
      }
    } catch (error) {
      console.error('[TripChecklist] Error adding weather-based suggestions:', error);
    }
  }, [checklist?.id, checklist?.items, generating, weatherBasedSuggestions]);

  const handleAddCityContextSuggestions = useCallback(async () => {
    if (!checklist || generating) return;
    if (!cityContextSuggestions.length) return;

    try {
      const existingTitles = new Set(checklist.items.map((i) => i.title.trim().toLowerCase()));
      const toAdd = cityContextSuggestions.filter((s) => !existingTitles.has(s.title.trim().toLowerCase()));
      if (!toAdd.length) return;

      for (const s of toAdd) {
        await addChecklistItem(checklist.id, {
          title: s.title,
          notes: s.notes,
          group: 'packing',
          category: 'essentials',
          completed: false,
          autoGenerated: true,
          source: 'city_context',
        });
      }
    } catch (error) {
      console.error('[TripChecklist] Error adding city-context suggestions:', error);
    }
  }, [checklist?.id, checklist?.items, generating, cityContextSuggestions]);

  const handleAddCityContextPlanningSuggestions = useCallback(async () => {
    if (!checklist || generating) return;
    if (!cityContextPlanningSuggestions.length) return;

    try {
      const existingTitles = new Set(checklist.items.map((i) => i.title.trim().toLowerCase()));
      const toAdd = cityContextPlanningSuggestions.filter(
        (s) => !existingTitles.has(s.title.trim().toLowerCase()),
      );
      if (!toAdd.length) return;

      for (const s of toAdd) {
        await addChecklistItem(checklist.id, {
          title: s.title,
          notes: s.notes,
          group: 'planning',
          category: s.category ?? 'documents',
          completed: false,
          autoGenerated: true,
          source: 'city_context',
        });
      }
    } catch (error) {
      console.error('[TripChecklist] Error adding city-context planning suggestions:', error);
    }
  }, [checklist?.id, checklist?.items, generating, cityContextPlanningSuggestions]);

  // Auto-generate checklist items when checklist is first created
  useEffect(() => {
    if (!checklist || !trip.id || generating) return;

    // Check if we have the expected minimum items
    // Planning checklist should have at least: visa, passport, insurance, wifi, medications, flight, lodging, transportation, activities
    // Packing list should have at least: clothing and essentials
    const planningItems = checklist.items.filter(item => item.group === 'planning' || !item.group);
    const packingItems = checklist.items.filter(item => item.group === 'packing');
    
    // Generate if checklist is empty or has very few items (might be incomplete generation)
    const shouldGenerate = checklist.items.length === 0 || 
      (planningItems.length < 5 && packingItems.length === 0);

    if (shouldGenerate) {
      generateChecklistItems();
    }
  }, [checklist?.id, trip.id, generateChecklistItems, generating]);

  // Always load weather for packing, even if we don't regenerate checklist items.
  // Previously, weatherSummary only updated when generateChecklistItems ran, which meant
  // existing checklists would never show weather-based packing hints.
  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    // Reset retry counter when trip changes
    setWeatherRetryAttempt(0);

    const load = async () => {
      if (!trip?.id) return;
      setWeatherLoading(true);
      setWeatherMessage(undefined);

      const weatherResult = await withTimeout(
        getWeatherForPacking(trip),
        12000,
        { forecast: null, isHistorical: false, error: 'timeout' } as any,
      );

      if (cancelled) return;

      const weather = weatherResult?.forecast || null;
      const isHistorical = weatherResult?.isHistorical;

      const forecastEntries: ForecastDay[] = Array.isArray(weather?.forecast)
        ? weather.forecast.filter(
            (f: ForecastDay) =>
              Number.isFinite(f?.temperature?.min) &&
              Number.isFinite(f?.temperature?.max)
          )
        : [];

      if (forecastEntries.length > 0) {
        const temps = forecastEntries.map((f: ForecastDay) => ({
          min: f.temperature.min,
          max: f.temperature.max,
        }));
        const avgMin = temps.reduce((sum: number, t) => sum + t.min, 0) / temps.length;
        const avgMax = temps.reduce((sum: number, t) => sum + t.max, 0) / temps.length;
        const avgTemp = (avgMin + avgMax) / 2;
        const minTemp = Math.min(...temps.map((t) => t.min));
        const maxTemp = Math.max(...temps.map((t) => t.max));
        const hasPrecipitation = forecastEntries.some(
          (f: ForecastDay) =>
            f.precipitation &&
            (f.precipitation.probability > 0 || (f.precipitation.amount || 0) > 0)
        );
        const conditions = forecastEntries
          .map((f: ForecastDay) => f.condition)
          .filter((c): c is string => !!c)
          .filter((v: string, i: number, a: string[]) => a.indexOf(v) === i);

        setWeatherSummary({
          avgTemp: Math.round(avgTemp),
          minTemp: Math.round(minTemp),
          maxTemp: Math.round(maxTemp),
          hasPrecipitation,
          conditions,
          isHistorical,
        });
        setWeatherMessage(undefined);
      } else {
        setWeatherSummary(undefined);
        if (weatherResult?.error === 'timeout') {
          setWeatherMessage('Weather is slow to load. We’ll refresh soon.');
        } else if (weatherResult?.error) {
          setWeatherMessage('Weather data is temporarily unavailable. We’ll refresh soon.');
        } else {
          setWeatherMessage('Forecast not available yet. We’ll update this as the trip approaches.');
        }
      }

      setWeatherLoading(false);

      // If we timed out / errored, retry a few times with backoff.
      // This makes the UX match the message "We'll refresh soon."
      const shouldRetry = weatherResult?.error === 'timeout' || !!weatherResult?.error;
      if (shouldRetry && weatherRetryAttempt < 3 && !cancelled) {
        const delays = [5000, 15000, 30000]; // 5s, 15s, 30s
        const delay = delays[weatherRetryAttempt] ?? 30000;
        retryTimer = setTimeout(() => {
          if (cancelled) return;
          setWeatherRetryAttempt((n) => n + 1);
          load();
        }, delay);
      }
    };

    load();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [
    trip?.id,
    trip?.destination,
    trip?.startDate,
    trip?.endDate,
    (trip as any)?.destinations?.[0]?.city,
    withTimeout,
  ]);

  // Update checklist items when bookings or documents change
  useEffect(() => {
    if (!checklist || generating) return;

    const updateItems = async () => {
      try {
        // Update flight booked status
        const flightItem = checklist.items.find(
          item => item.title === 'Flight booked' && item.source === 'auto'
        );
        const hasFlight = bookings.some(b => b.type === 'flight');
        if (flightItem && flightItem.completed !== hasFlight) {
          const flightBooking = bookings.find(b => b.type === 'flight');
          const updateData: any = {
            completed: hasFlight,
            source: hasFlight ? 'booking' : 'auto',
          };
          if (flightBooking?.id) {
            updateData.sourceId = flightBooking.id;
          }
          await updateChecklistItem(checklist.id, flightItem.id, updateData);
        }

        // Update lodging booked status
        const hotelItem = checklist.items.find(
          item => item.title === 'Lodging booked' && item.source === 'auto'
        );
        const hasHotel = bookings.some(b => b.type === 'hotel');
        if (hotelItem && hotelItem.completed !== hasHotel) {
          const hotelBooking = bookings.find(b => b.type === 'hotel');
          const updateData: any = {
            completed: hasHotel,
            source: hasHotel ? 'booking' : 'auto',
          };
          if (hotelBooking?.id) {
            updateData.sourceId = hotelBooking.id;
          }
          await updateChecklistItem(checklist.id, hotelItem.id, updateData);
        }

        // Update transportation status
        const transportItem = checklist.items.find(
          item => item.title === 'Transportation mode planned' && item.source === 'auto'
        );
        const hasTransport = bookings.some(b => ['car', 'transportation', 'ferry'].includes(b.type));
        if (transportItem && transportItem.completed !== hasTransport) {
          const transportBooking = bookings.find(b => ['car', 'transportation', 'ferry'].includes(b.type));
          const updateData: any = {
            completed: hasTransport,
            source: hasTransport ? 'booking' : 'auto',
          };
          if (transportBooking?.id) {
            updateData.sourceId = transportBooking.id;
          }
          await updateChecklistItem(checklist.id, transportItem.id, updateData);
        }

        // Update activities status
        const activitiesItem = checklist.items.find(
          item => item.title === 'Activities and tours reserved' && item.source === 'auto'
        );
        const hasActivities = bookings.some(b => ['activity', 'tour', 'attraction'].includes(b.type));
        if (activitiesItem && activitiesItem.completed !== hasActivities) {
          const activityCount = bookings.filter(b => ['activity', 'tour', 'attraction'].includes(b.type)).length;
          await updateChecklistItem(checklist.id, activitiesItem.id, {
            completed: hasActivities,
            source: hasActivities ? 'booking' : 'auto',
            notes: hasActivities
              ? `${activityCount} ${activityCount === 1 ? 'activity' : 'activities'} booked`
              : 'Reserve activities and tours',
          });
        }

        // Update travel insurance status
        const insuranceItem = checklist.items.find(
          item => item.title === 'Travel insurance (optional)' && item.source === 'auto'
        );
        const hasInsurance = documents.some(d => d.type === 'insurance');
        if (insuranceItem && insuranceItem.completed !== hasInsurance) {
          await updateChecklistItem(checklist.id, insuranceItem.id, {
            completed: hasInsurance,
            source: hasInsurance ? 'document' : 'auto',
            sourceId: hasInsurance ? documents.find(d => d.type === 'insurance')?.id : undefined,
          });
        }

        // Update passport status
        const passportItem = checklist.items.find(
          item => item.title === 'Passport/ID ready and valid' && item.source === 'auto'
        );
        const passport = documents.find(d => d.type === 'passport');
        if (passportItem && passport) {
          const tripEnd = trip.endDate instanceof Date ? trip.endDate : new Date(trip.endDate);
          const sixMonthsAfterTrip = new Date(tripEnd);
          sixMonthsAfterTrip.setMonth(sixMonthsAfterTrip.getMonth() + 6);
          const passportExp = passport.expiresOn;
          const isValid = passportExp ? passportExp > sixMonthsAfterTrip : false;
          
          if (passportItem.completed !== isValid) {
            await updateChecklistItem(checklist.id, passportItem.id, {
              completed: isValid,
              source: 'document',
              sourceId: passport.id,
              notes: passportExp && passportExp <= sixMonthsAfterTrip 
                ? `⚠️ Passport expires ${passportExp.toLocaleDateString()}. Most countries require 6+ months validity.`
                : 'Passport is valid for travel',
            });
          }
        }
      } catch (error) {
        console.error('[TripChecklist] Error updating items:', error);
      }
    };

    updateItems();
  }, [checklist?.id, bookings, documents, trip.endDate, generating]);

  // Create checklist if it doesn't exist
  useEffect(() => {
    if (!checklist && !loading && trip.id) {
      createChecklist(trip.id, userId, 'Trip Checklist')
        .then(() => {
          // Checklist will be loaded via subscription
        })
        .catch((error) => {
          console.error('[TripChecklist] Error creating checklist:', error);
        });
    }
  }, [checklist, loading, trip.id, userId]);

  const handleToggleItem = useCallback(async (itemId: string) => {
    if (!checklist) return;

    const item = checklist.items.find(i => i.id === itemId);
    if (!item) return;

    try {
      await updateChecklistItem(checklist.id, itemId, {
        completed: !item.completed,
      });
    } catch (error) {
      console.error('[TripChecklist] Error toggling item:', error);
    }
  }, [checklist]);

  const handleAddItem = useCallback((group: 'planning' | 'packing') => {
    setAddItemGroup(group);
    setShowAddItemModal(true);
  }, []);

  const handleSubmitCustomItem = useCallback(async (item: {
    title: string;
    notes?: string;
    dueDate?: Date;
    category?: ChecklistItem['category'];
    group: 'planning' | 'packing';
  }) => {
    if (!checklist) return;

    try {
      await addChecklistItem(checklist.id, {
        title: item.title,
        notes: item.notes,
        dueDate: item.dueDate,
        category: item.category,
        group: item.group,
        completed: false,
        autoGenerated: false,
        source: 'manual',
      });
    } catch (error) {
      console.error('[TripChecklist] Error adding custom item:', error);
      throw error;
    }
  }, [checklist]);

  if (loading || !checklist) {
    return (
      <View style={{ padding: theme.spacing(2), alignItems: 'center' }}>
        <ActivityIndicator size="small" color={theme.colors.primary} />
      </View>
    );
  }

  const planningItems = checklist.items.filter((i) => (i.group ?? 'planning') === 'planning');
  const pendingPlanningCount = planningItems.filter((i) => !i.completed).length;
  const completedPlanningCount = planningItems.filter((i) => i.completed).length;
  const planningCompletion = planningItems.length ? completedPlanningCount / planningItems.length : 0;
  const hasFlight = bookings.some((b) => (b.type || '').toLowerCase() === 'flight');
  const hasLodging = bookings.some((b) => (b.type || '').toLowerCase() === 'hotel');
  const activitiesItems = planningItems.filter((i) => i.category === 'activities');
  const hasActivities = activitiesItems.length > 0 && activitiesItems.some((i) => i.completed);

  const readinessLine = [
    `Flights ${hasFlight ? '✔' : '⏳'}`,
    `Lodging ${hasLodging ? '✔' : '⏳'}`,
    `Activities ${hasActivities ? '✔' : '⏳'}`,
  ].join('  ');

  const destinationLabel = (trip.destination || trip.destinations?.[0]?.city || trip.name || '').toString().trim();
  const cityContextPlaceLabel = getCityContextPlaceLabel(trip);
  const friendlyLine =
    planningCompletion >= 0.8
      ? "You're almost ready."
      : destinationLabel
        ? `${destinationLabel} is coming together.`
        : undefined;

  return (
    <>
      <View style={{ gap: theme.spacing(3) }}>
        <Card variant="outlined" padding={theme.spacing(1.5)}>
          <View style={{ gap: theme.spacing(0.5) }}>
            <ThemedText variant="bodyStrong" style={{ fontSize: 14 }}>
              Trip readiness
            </ThemedText>
            <Caption color={theme.colors.textSecondary}>{readinessLine}</Caption>
            <Caption color={theme.colors.textSecondary}>
              {pendingPlanningCount} thing{pendingPlanningCount === 1 ? '' : 's'} left to plan
            </Caption>
            {!!friendlyLine && (
              <Caption style={{ fontWeight: '600' }}>{friendlyLine}</Caption>
            )}
          </View>
        </Card>
        <PlanningChecklistSection
          items={checklist.items}
          onToggleItem={handleToggleItem}
          onAddItem={() => handleAddItem('planning')}
          loading={generating}
          cityContextPlaceLabel={cityContextPlaceLabel}
          cityContextPlanningSuggestions={cityContextPlanningSuggestions}
          onAddCityContextPlanningSuggestions={handleAddCityContextPlanningSuggestions}
          missingCityContextPlanningSuggestionsCount={missingCityContextPlanningSuggestionsCount}
        />
        <PackingListSection
          items={checklist.items}
          onToggleItem={handleToggleItem}
          onAddItem={() => handleAddItem('packing')}
          loading={generating}
          weatherSummary={weatherSummary}
          weatherMessage={weatherMessage}
          weatherBasedSuggestions={weatherBasedSuggestions}
          onAddWeatherBasedSuggestions={handleAddWeatherBasedSuggestions}
          missingWeatherBasedSuggestionsCount={missingWeatherBasedSuggestionsCount}
          cityContextPlaceLabel={cityContextPlaceLabel}
          cityContextSuggestions={cityContextSuggestions}
          onAddCityContextSuggestions={handleAddCityContextSuggestions}
          missingCityContextSuggestionsCount={missingCityContextSuggestionsCount}
        />
      </View>

      <AddChecklistItemModal
        visible={showAddItemModal}
        onClose={() => setShowAddItemModal(false)}
        onSubmit={handleSubmitCustomItem}
        group={addItemGroup}
      />
    </>
  );
}

